import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowForwardOutlined, CheckCircleOutline, Phone, BusinessCenterOutlined, ShowChartOutlined } from '@mui/icons-material';
import TrustedBy from '../components/TrustedBy';
import SEOHead from '../components/SEOHead';

const services = [
  {
    icon: <BusinessCenterOutlined sx={{ fontSize: 36, color: '#123D6B' }} />,
    title: 'Startup Registration',
    description: 'Complete business registration for Pvt Ltd, LLP, Proprietorship, and Trademark protection.',
    ctaText: 'Start your business',
    link: '/startup-registration',
  },
  {
    icon: <ShowChartOutlined sx={{ fontSize: 36, color: '#123D6B' }} />,
    title: 'Tax Planning',
    description: 'Strategic tax planning solutions for optimal legal tax savings for individuals and businesses.',
    ctaText: 'Optimize Tax Strategy',
    link: '/tax-planning',
  },
];

const Home: React.FC = () => (
  <Box sx={{ bgcolor: '#F8FAFC' }}>
    <SEOHead
      title="Fingard Partners | Expert Tax, GST & Compliance Services in India"
      description="Your trusted CA firm for Income Tax Filing, GST Registration, Startup Incorporation, MCA Compliance & Tax Planning. Book a free consultation today."
      keywords="CA firm India, Income Tax Filing, GST Registration, Startup Registration, Tax Planning, MCA Compliance, Chartered Accountant"
      canonicalUrl="/"
    />
    {/* Hero Banner Section */}
    <Box sx={{ py: { xs: 8, md: 11 }, background: 'linear-gradient(130deg, #0B2847 0%, #123D6B 60%, #2A5A8D 100%)', color: '#fff' }}>
      <Container maxWidth="lg">
        <Grid container spacing={5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" component="h1" sx={{ fontSize: { xs: '2rem', md: '3.2rem' }, mb: 2, fontWeight: 800, letterSpacing: '-0.02em' }}>
              Modern financial and compliance support for growing businesses.
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.92, fontSize: '16px', lineHeight: 1.6 }}>
              Fingard helps individuals, startups, and enterprises with taxation, compliance,
              and strategic advisory services built on precision and trust.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button component={RouterLink} to="/contact" variant="contained" disableElevation color="secondary" startIcon={<Phone />} sx={{ fontWeight: 600, textTransform: 'none', px: 3, py: 1.2, borderRadius: '8px' }}>
                Book Consultation
              </Button>
              <Button component={RouterLink} to="/services" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'none', px: 3, py: 1.2, borderRadius: '8px', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                Explore Services
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="/images/hero-financial-growth.png"
              alt="Professional CA and financial planning support"
              sx={{ width: '100%', borderRadius: 4, bgcolor: '#fff', p: 1.5, boxShadow: '0 20px 40px rgba(0,0,0,0.25)' }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* FIXED BACKGROUND: Adjusted to a rich corporate ice-blue gradient so white frost glass stands out perfectly */}
    <Box 
      sx={{ 
        py: { xs: 10, md: 14 },
        background: 'linear-gradient(180deg, #EDF4FA 0%, #DCE6F1 100%)', 
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'2\' cy=\'2\' r=\'1.5\' fill=\'%230b2847\' fill-opacity=\'0.06\'/%3E%3C/svg%3E")',
          pointerEvents: 'none',
        },
        // Enhanced background radial lighting flare to emphasize glass layer reflections
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '500px',
          height: '500px',
          top: '15%',
          left: 'calc(50% - 250px)',
          background: 'radial-gradient(circle, rgba(42, 90, 141, 0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
          zIndex: 1,
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box textAlign="center" mb={6}>
          <Typography 
            variant="h3" 
            sx={{ mb: 1.5, fontWeight: 800, color: '#123D6B', letterSpacing: '-0.02em' }}
          >
            Core Services
          </Typography>
          <Typography sx={{ color: '#475569', fontWeight: 600 }}>
            End-to-end support from filing to advisory handled in real-time.
          </Typography>
        </Box>
        
        <Grid container spacing={3} justifyContent="center">
          {services.map((service) => (
            <Grid item xs={12} sm={8} md={6} key={service.title}>
              <Card 
                sx={{ 
                  height: '100%', 
                  borderRadius: '28px',
                  // PREMIUM HIGH-CONTRAST FROST GLASSMORPHISM Layout:
                  background: 'rgba(255, 255, 255, 0.45)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255, 255, 255, 0.7)',
                  boxShadow: '0px 20px 45px rgba(11, 40, 71, 0.07)',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                  '&:hover': { 
                    transform: 'translateY(-6px)',
                    borderColor: '#C59B27', 
                    background: 'rgba(255, 255, 255, 0.75)',
                    boxShadow: '0px 30px 60px rgba(11, 40, 71, 0.12)',
                    '& .cta-text': { color: '#C59B27' },
                    '& .arrow-icon': { transform: 'translateX(5px)', color: '#C59B27' }
                  } 
                }}
              >
                <CardActionArea component={RouterLink} to={service.link} sx={{ height: '100%', p: 2 }}>
                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ mb: 2.5, display: 'inline-flex', p: 1.5, borderRadius: '14px', bgcolor: 'rgba(11, 40, 71, 0.06)', width: 'fit-content' }}>
                      {service.icon}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 800, color: '#123D6B' }}>
                      {service.title}
                    </Typography>
                    <Typography sx={{ color: '#475569', lineHeight: 1.6, mb: 4, fontSize: '14px', fontWeight: 500 }}>
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', pt: 2, borderTop: '1px solid rgba(11, 40, 71, 0.08)' }}>
                      <Typography 
                        className="cta-text"
                        variant="subtitle2" 
                        sx={{ fontWeight: 700, color: '#123D6B', fontSize: '14px', transition: 'color 0.2s ease-in-out' }}
                      >
                        {service.ctaText}
                      </Typography>
                      <ArrowForwardOutlined 
                        className="arrow-icon"
                        sx={{ fontSize: 16, color: '#123D6B', transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out' }} 
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    {/* Structural Trust Points Section */}
    <Box sx={{ py: 10, bgcolor: '#FFFFFF' }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {[
            'Qualified chartered accountants and specialists',
            'Transparent quotation with clear deliverables',
            'Fast turnaround and proactive communication',
          ].map((point) => (
            <Grid item xs={12} md={4} key={point}>
              <Paper sx={{ p: 4, height: '100%', border: '1px solid', borderColor: '#E2E8F0', borderRadius: '16px', boxShadow: 'none', bgcolor: '#F8FAFC' }}>
                <CheckCircleOutline color="primary" sx={{ mb: 1.5, fontSize: 28 }} />
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: '#123D6B', fontSize: '17px' }}>{point}</Typography>
                <Typography sx={{ color: '#64748B', fontSize: '14px', lineHeight: 1.6 }}>
                  Our process is built for confidence, compliance, and long-term client relationships.
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <TrustedBy />
  </Box>
);

export default Home;
